import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  getFeatureKeys,
  getFeatureGroups,
  addFeatureGroup,
  updateFeatureGroup,
  deleteFeatureGroup,
  addFeatureKey,
  updateFeatureKey,
  deleteFeatureKey,
  getCompanies,
  updateCompany,
  deleteField,
  Timestamp,
} from '@/lib/firebase';
import { Textarea } from '@/components/ui/textarea';
import { Video, Key, Plus, Edit, Loader2, ChevronRight, ChevronLeft, Search, Layers, Trash2 } from 'lucide-react';
import { logger } from '@/lib/logger';

// ─── Interfaces ──────────────────────────────────────────────────────────────

interface FeatureGroup {
  id: string;
  name: string;
  tnc?: string;
}

interface FeatureKey {
  id: string;
  name: string;
  keyValue: string;
  description: string;
  enabled: boolean;
  terms?: string;
  url?: string;
  displayOrder?: number;
  featureGroup?: string;
}

interface Company {
  id: string;
  name: string;
  featureKeys?: string[];
  features?: Array<{ featureGroup: string; featureKeys: string[] }>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Derives a camelCase group key from a display name, e.g. "UK Helpline Support" → "ukHelplineSupport" */
const toGroupKey = (name: string): string =>
  name.trim()
    .split(/\s+/)
    .filter(w => w)
    .map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');

type FeaturesArray = Array<{ featureGroup: string; featureKeys: string[] }>;

function addFkToFeatures(features: FeaturesArray, fkId: string, fgId: string): FeaturesArray {
  if (fgId === undefined || fgId === null) return features;
  const idx = features.findIndex(f => f.featureGroup === fgId);
  if (idx >= 0) {
    if (features[idx].featureKeys.includes(fkId)) return features;
    return features.map((f, i) => i === idx ? { ...f, featureKeys: [...f.featureKeys, fkId] } : f);
  }
  return [...features, { featureGroup: fgId, featureKeys: [fkId] }];
}

function removeFkFromFeatures(features: FeaturesArray, fkId: string, fgId: string): FeaturesArray {
  return features
    .map(f => f.featureGroup === fgId ? { ...f, featureKeys: f.featureKeys.filter(k => k !== fkId) } : f)
    .filter(f => f.featureKeys.length > 0);
}

/**
 * Converts a company's stored features (old or new format) to a canonical FeaturesArray.
 * - New format: company.features[] — returned as-is, with any legacy keyValues merged in.
 * - Old format: company.featureKeys string[] of keyValues — mapped to doc IDs via kvToGroup.
 * kvToGroup: Map<keyValue, { id: string; featureGroup: string }>
 */
function normalizeCompanyFeatures(
  company: Company,
  kvToGroup: Map<string, { id: string; featureGroup: string }>
): FeaturesArray {
  if (company.features && company.features.length > 0) {
    const alreadyCovered = new Set(company.features.flatMap(f => f.featureKeys));
    let result: FeaturesArray = company.features.map(f => ({ ...f, featureKeys: [...f.featureKeys] }));
    if (Array.isArray(company.featureKeys)) {
      for (const kv of company.featureKeys) {
        const meta = kvToGroup.get(kv);
        if (meta && !alreadyCovered.has(meta.id)) {
          result = addFkToFeatures(result, meta.id, meta.featureGroup ?? '');
          alreadyCovered.add(meta.id);
        }
      }
    }
    return result;
  }
  if (Array.isArray(company.featureKeys) && company.featureKeys.length > 0) {
    let result: FeaturesArray = [];
    for (const kv of company.featureKeys) {
      const meta = kvToGroup.get(kv);
      if (meta) result = addFkToFeatures(result, meta.id, meta.featureGroup ?? '');
    }
    return result;
  }
  return [];
}

// ─── Settings Page ────────────────────────────────────────────────────────────

export default function Settings() {
  const [featureKeys, setFeatureKeys] = useState<FeatureKey[]>([]);
  const [loadingFeatureKeys, setLoadingFeatureKeys] = useState(true);
  const [featureGroups, setFeatureGroups] = useState<FeatureGroup[]>([]);
  const [loadingFeatureGroups, setLoadingFeatureGroups] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalFeatureKey, setEditModalFeatureKey] = useState<FeatureKey | null>(null);
  const [addGroupModalOpen, setAddGroupModalOpen] = useState(false);
  const [editGroupFG, setEditGroupFG] = useState<FeatureGroup | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const loadFKs = async () => {
      try {
        const data = await getFeatureKeys();
        setFeatureKeys(data.map(raw => {
          const name: string = raw.name || '';
          return { ...raw, keyValue: raw.keyValue || name.trim().toLowerCase().replace(/\s+/g, '') } as FeatureKey;
        }));
      } catch (error: any) {
        logger.error('Error loading feature keys:', error);
      } finally {
        setLoadingFeatureKeys(false);
      }
    };
    loadFKs();
    const timer1 = setInterval(loadFKs, 15_000);

    const loadFGs = async () => {
      try {
        const data = await getFeatureGroups();
        setFeatureGroups(data as FeatureGroup[]);
      } catch (error: any) {
        logger.error('Error loading feature groups:', error);
      } finally {
        setLoadingFeatureGroups(false);
      }
    };
    loadFGs();
    const timer2 = setInterval(loadFGs, 15_000);

    return () => { clearInterval(timer1); clearInterval(timer2); };
  }, []);

  return (
    <div className="flex-1 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Settings</h1>
          <p className="text-slate-600">Manage your application settings</p>
        </div>

        <div className="grid gap-6">
          {/* Vimeo Import Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Video className="w-5 h-5" />
                <span>Video Import Settings</span>
              </CardTitle>
              <CardDescription>
                Vimeo is configured as the video import source
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600">
                Videos are imported from Vimeo using the configured OAuth credentials. Use the
                Vimeo import page to process and import new videos.
              </p>
            </CardContent>
          </Card>

          {/* Feature Groups */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Layers className="w-5 h-5" />
                    <span>Feature Groups</span>
                  </CardTitle>
                  <CardDescription>
                    Organise feature keys into logical groups
                  </CardDescription>
                </div>
                <Button onClick={() => setAddGroupModalOpen(true)} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Feature Group
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingFeatureGroups ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading feature groups...
                </div>
              ) : featureGroups.length === 0 ? (
                <div className="text-center py-8">
                  <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No feature groups defined yet.</p>
                  <p className="text-slate-400 text-xs mt-1">Add a feature group to get started.</p>
                </div>
              ) : (
                <div className="divide-y rounded-md border">
                  {featureGroups.map((fg) => (
                    <div key={fg.id} className="flex items-center justify-between px-4 py-3 gap-4">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm text-slate-900">{fg.name}</span>
                        <p className="text-xs text-slate-400 mt-0.5 font-mono">{fg.id}</p>
                        {fg.tnc && (
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{fg.tnc}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => setEditGroupFG(fg)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feature Keys */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Key className="w-5 h-5" />
                    <span>Feature Keys</span>
                  </CardTitle>
                  <CardDescription>
                    Define app features and control which companies have them enabled
                  </CardDescription>
                </div>
                <Button onClick={() => setAddModalOpen(true)} className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Feature Key
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingFeatureKeys ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading feature keys...
                </div>
              ) : featureKeys.length === 0 ? (
                <div className="text-center py-8">
                  <Key className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">No feature keys defined yet.</p>
                  <p className="text-slate-400 text-xs mt-1">Add a feature key to get started.</p>
                </div>
              ) : (
                <div className="divide-y rounded-md border">
                  {featureKeys.map((fk) => {
                    const group = featureGroups.find(g => g.id === fk.featureGroup);
                    return (
                      <div key={fk.id} className="flex items-center justify-between px-4 py-3 gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-slate-900">{fk.name}</span>
                            <Badge
                              className={fk.enabled
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                              }
                            >
                              {fk.enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                            {group && (
                              <Badge className="bg-indigo-50 text-indigo-700 border-indigo-100 text-xs">
                                {group.name}
                              </Badge>
                            )}
                          </div>
                          {fk.keyValue && (
                            <p className="text-xs text-slate-400 mt-0.5 font-mono">{fk.keyValue}</p>
                          )}
                          {fk.description && (
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{fk.description}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setEditModalFeatureKey(fk)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {addGroupModalOpen && (
        <AddFeatureGroupModal
          isOpen={addGroupModalOpen}
          onClose={() => setAddGroupModalOpen(false)}
          toast={toast}
        />
      )}

      {editGroupFG && (
        <EditFeatureGroupModal
          isOpen={!!editGroupFG}
          onClose={() => setEditGroupFG(null)}
          featureGroup={editGroupFG}
          featureKeys={featureKeys}
          toast={toast}
        />
      )}

      {addModalOpen && (
        <AddFeatureKeyModal
          isOpen={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          featureGroups={featureGroups}
          featureKeysMeta={featureKeys}
          toast={toast}
        />
      )}

      {editModalFeatureKey && (
        <EditFeatureKeyModal
          isOpen={!!editModalFeatureKey}
          onClose={() => setEditModalFeatureKey(null)}
          featureKey={editModalFeatureKey}
          featureGroups={featureGroups}
          featureKeysMeta={featureKeys}
          toast={toast}
        />
      )}
    </div>
  );
}

// ─── AddFeatureGroupModal ─────────────────────────────────────────────────────

interface AddFeatureGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  toast: any;
}

function AddFeatureGroupModal({ isOpen, onClose, toast }: AddFeatureGroupModalProps) {
  const [name, setName] = useState('');
  const [tnc, setTnc] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const derivedKey = toGroupKey(name);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await addFeatureGroup(derivedKey, {
        name: name.trim(),
        ...(tnc.trim() ? { tnc: tnc.trim() } : {}),
      });
      toast({ title: 'Feature group created', description: `"${name.trim()}" has been added.` });
      onClose();
    } catch (err: any) {
      logger.error('Error creating feature group:', err);
      toast({ title: 'Failed to create feature group', description: err.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => { if (!isSubmitting) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Add Feature Group
          </DialogTitle>
          <DialogDescription>
            Create a new group to organise related feature keys.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fg-name">Name <span className="text-red-500">*</span></Label>
            <Input
              id="fg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Virtual Appointment"
              disabled={isSubmitting}
              className={errors.name ? 'border-red-500' : ''}
            />
            {name.trim() && (
              <p className="text-xs text-slate-500">
                Group key: <code className="font-mono bg-slate-100 px-1 rounded">{derivedKey}</code>
              </p>
            )}
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="fg-tnc">Terms &amp; Conditions</Label>
            <Textarea
              id="fg-tnc"
              value={tnc}
              onChange={(e) => setTnc(e.target.value)}
              placeholder="Optional terms and conditions for this group"
              disabled={isSubmitting}
              rows={4}
            />
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Feature Group'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── EditFeatureGroupModal ────────────────────────────────────────────────────

interface EditFeatureGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureGroup: FeatureGroup;
  featureKeys: FeatureKey[];
  toast: any;
}

function EditFeatureGroupModal({ isOpen, onClose, featureGroup, featureKeys, toast }: EditFeatureGroupModalProps) {
  const [name, setName] = useState(featureGroup.name);
  const [tnc, setTnc] = useState(featureGroup.tnc ?? '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteStep, setDeleteStep] = useState<null | 'confirm' | 'has-keys'>(null);

  const affectedKeys = featureKeys.filter(fk => fk.featureGroup === featureGroup.id);

  useEffect(() => {
    setName(featureGroup.name);
    setTnc(featureGroup.tnc ?? '');
    setDeleteStep(null);
  }, [featureGroup]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await updateFeatureGroup(featureGroup.id, {
        name: name.trim(),
        ...(tnc.trim() ? { tnc: tnc.trim() } : { tnc: '' }),
      });
      toast({ title: 'Feature group updated', description: `"${name.trim()}" has been updated.` });
      onClose();
    } catch (err: any) {
      logger.error('Error updating feature group:', err);
      toast({ title: 'Failed to update feature group', description: err.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = () => {
    if (affectedKeys.length > 0) {
      setDeleteStep('has-keys');
    } else {
      setDeleteStep('confirm');
    }
  };

  const handleDeleteConfirm = async (unassignKeys: boolean) => {
    setIsSubmitting(true);
    try {
      if (unassignKeys && affectedKeys.length > 0) {
        await Promise.all(affectedKeys.map(fk => updateFeatureKey(fk.id, { featureGroup: deleteField() })));
      }
      await deleteFeatureGroup(featureGroup.id);
      toast({ title: 'Feature group deleted', description: `"${featureGroup.name}" has been removed.` });
      onClose();
    } catch (err: any) {
      logger.error('Error deleting feature group:', err);
      toast({ title: 'Failed to delete feature group', description: err.message, variant: 'destructive' });
      setDeleteStep(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => { if (!isSubmitting) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Edit Feature Group
          </DialogTitle>
          <DialogDescription>
            Update this feature group's display name or terms.
          </DialogDescription>
        </DialogHeader>

        {deleteStep === 'confirm' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              Are you sure you want to delete <strong>{featureGroup.name}</strong>? This cannot be undone.
            </p>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDeleteStep(null)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDeleteConfirm(false)}
                disabled={isSubmitting}
              >
                {isSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Delete Group'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {deleteStep === 'has-keys' && (
          <div className="space-y-4">
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-2">
              <p className="text-sm font-medium text-amber-800">
                {affectedKeys.length} feature key{affectedKeys.length > 1 ? 's are' : ' is'} assigned to this group:
              </p>
              <ul className="text-sm text-amber-700 space-y-1 max-h-40 overflow-y-auto">
                {affectedKeys.map(fk => (
                  <li key={fk.id} className="flex items-center gap-1.5">
                    <Key className="h-3 w-3 flex-shrink-0" />
                    <span>{fk.name}</span>
                    <code className="text-xs font-mono text-amber-600">({fk.keyValue})</code>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-sm text-slate-600">
              Deleting this group will unassign these keys, moving them to "Other". Do you want to proceed?
            </p>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDeleteStep(null)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => handleDeleteConfirm(true)}
                disabled={isSubmitting}
              >
                {isSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Unassign & Delete'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {deleteStep === null && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="efg-name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="efg-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
              <p className="text-xs text-slate-500">
                Group key (immutable): <code className="font-mono bg-slate-100 px-1 rounded">{featureGroup.id}</code>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="efg-tnc">Terms &amp; Conditions</Label>
              <Textarea
                id="efg-tnc"
                value={tnc}
                onChange={(e) => setTnc(e.target.value)}
                placeholder="Optional terms and conditions for this group"
                disabled={isSubmitting}
                rows={4}
              />
            </div>

            <DialogFooter className="gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mr-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleDeleteClick}
                disabled={isSubmitting}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete Group
              </Button>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── AddFeatureKeyModal ───────────────────────────────────────────────────────

interface AddFeatureKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureGroups: FeatureGroup[];
  featureKeysMeta: FeatureKey[];
  toast: any;
}

function AddFeatureKeyModal({ isOpen, onClose, featureGroups, featureKeysMeta, toast }: AddFeatureKeyModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [terms, setTerms] = useState('');
  const [url, setUrl] = useState('');
  const [displayOrder, setDisplayOrder] = useState<number | ''>('');
  const [featureGroup, setFeatureGroup] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [strategy, setStrategy] = useState<'disable_all' | 'enable_all' | 'enable_selection'>('disable_all');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<Set<string>>(new Set());
  const [companySearch, setCompanySearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (strategy === 'enable_selection' && companies.length === 0) {
      loadCompanies();
    }
  }, [strategy]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const data = await getCompanies() as any[];
      setCompanies(data.map(d => ({
        id: d.id,
        name: d.name || 'Unnamed',
        featureKeys: d.featureKeys || [],
        features: d.features || [],
      })));
    } catch (err: any) {
      logger.error('Error loading companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const kv = name.trim().toLowerCase().replace(/\s+/g, '');
      const fgId = featureGroup;

      const newFkId = await addFeatureKey({
        name: name.trim(),
        keyValue: kv,
        description: description.trim(),
        terms: terms.trim(),
        url: url.trim(),
        displayOrder: displayOrder === '' ? null : Number(displayOrder),
        featureGroup: fgId,
        enabled,
        created: Timestamp.now(),
      });

      // Build keyValue → {id, featureGroup} lookup for legacy normalization
      const kvToGroup = new Map(featureKeysMeta.map(fk => [fk.keyValue, { id: fk.id, featureGroup: fk.featureGroup ?? '' }]));

      // Determine which companies to enable
      let companiesToEnable: Company[] = [];
      if (strategy === 'enable_all') {
        let all = companies;
        if (all.length === 0) {
          const data = await getCompanies() as any[];
          all = data.map(d => ({
            id: d.id,
            name: d.name || '',
            featureKeys: d.featureKeys || [],
            features: d.features || [],
          }));
        }
        companiesToEnable = all;
      } else if (strategy === 'enable_selection') {
        companiesToEnable = companies.filter(c => selectedCompanyIds.has(c.id));
      }

      if (companiesToEnable.length > 0) {
        await Promise.all(companiesToEnable.map(company => {
          const normalized = normalizeCompanyFeatures(company, kvToGroup);
          const newFeatures = addFkToFeatures(normalized, newFkId, fgId ?? '');
          return updateCompany(company.id, { features: newFeatures });
        }));
      }

      toast({ title: 'Feature key created', description: `"${name.trim()}" has been added.` });
      onClose();
    } catch (err: any) {
      logger.error('Error creating feature key:', err);
      toast({ title: 'Failed to create feature key', description: err.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={() => { if (!isSubmitting) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Add Feature Key
          </DialogTitle>
          <DialogDescription>
            Define a new feature and choose which companies start with it enabled.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fk-name">Name <span className="text-red-500">*</span></Label>
            <Input
              id="fk-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Advanced Analytics"
              disabled={isSubmitting}
              className={errors.name ? 'border-red-500' : ''}
            />
            {name.trim() && (
              <p className="text-xs text-slate-500">
                Key value: <code className="font-mono bg-slate-100 px-1 rounded">{name.trim().toLowerCase().replace(/\s+/g, '')}</code>
              </p>
            )}
            {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="fk-description">Description</Label>
            <Textarea
              id="fk-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this feature"
              disabled={isSubmitting}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fk-terms">Terms &amp; Conditions</Label>
            <Textarea
              id="fk-terms"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder="Long terms and conditions text"
              disabled={isSubmitting}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fk-url">URL</Label>
              <Input
                id="fk-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="e.g. serviceurltext"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fk-display-order">Display Order</Label>
              <Input
                id="fk-display-order"
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g. 1"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fk-feature-group">Feature Group</Label>
            {featureGroups.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Add a Feature Group first to assign this key to one.</p>
            ) : (
              <Select value={featureGroup || 'none'} onValueChange={(v) => setFeatureGroup(v === 'none' ? '' : v)} disabled={isSubmitting}>
                <SelectTrigger id="fk-feature-group">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No group</SelectItem>
                  {featureGroups.map(fg => (
                    <SelectItem key={fg.id} value={fg.id}>{fg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <Label htmlFor="fk-enabled" className="text-sm font-medium">Enabled</Label>
              <p className="text-xs text-slate-500 mt-0.5">Whether this feature key is active</p>
            </div>
            <Switch
              id="fk-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-3">
            <Label>Existing Companies</Label>
            <RadioGroup
              value={strategy}
              onValueChange={(v) => setStrategy(v as any)}
              disabled={isSubmitting}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="disable_all" id="s-disable" />
                <Label htmlFor="s-disable" className="font-normal cursor-pointer">Disable for all companies</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="enable_all" id="s-enable-all" />
                <Label htmlFor="s-enable-all" className="font-normal cursor-pointer">Enable for all companies</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="enable_selection" id="s-selection" />
                <Label htmlFor="s-selection" className="font-normal cursor-pointer">Enable for selected companies</Label>
              </div>
            </RadioGroup>
          </div>

          {strategy === 'enable_selection' && (
            <div className="space-y-2">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  placeholder="Search companies..."
                  className="pl-10"
                  disabled={isSubmitting || loadingCompanies}
                />
              </div>
              {loadingCompanies ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading companies...
                </div>
              ) : (
                <div className="rounded-md border max-h-48 overflow-y-auto divide-y">
                  {filteredCompanies.length === 0 ? (
                    <p className="text-sm text-slate-400 px-3 py-2">No companies found</p>
                  ) : (
                    filteredCompanies.map(c => (
                      <div key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
                        <input
                          type="checkbox"
                          id={`company-${c.id}`}
                          checked={selectedCompanyIds.has(c.id)}
                          onChange={(e) => {
                            const next = new Set(selectedCompanyIds);
                            if (e.target.checked) next.add(c.id);
                            else next.delete(c.id);
                            setSelectedCompanyIds(next);
                          }}
                          disabled={isSubmitting}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor={`company-${c.id}`} className="font-normal cursor-pointer text-sm flex-1">
                          {c.name}
                        </Label>
                      </div>
                    ))
                  )}
                </div>
              )}
              {selectedCompanyIds.size > 0 && (
                <p className="text-xs text-slate-500">{selectedCompanyIds.size} company/companies selected</p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : 'Save Feature Key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── EditFeatureKeyModal ──────────────────────────────────────────────────────

interface EditFeatureKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureKey: FeatureKey;
  featureGroups: FeatureGroup[];
  featureKeysMeta: FeatureKey[];
  toast: any;
}

function EditFeatureKeyModal({ isOpen, onClose, featureKey, featureGroups, featureKeysMeta, toast }: EditFeatureKeyModalProps) {
  const [name, setName] = useState(featureKey.name);
  const [description, setDescription] = useState(featureKey.description);
  const [terms, setTerms] = useState(featureKey.terms ?? '');
  const [url, setUrl] = useState(featureKey.url ?? '');
  const [displayOrder, setDisplayOrder] = useState<number | ''>(featureKey.displayOrder ?? '');
  const [featureGroup, setFeatureGroup] = useState<string>(() => {
    const valid = featureGroups.find(g => g.id === featureKey.featureGroup);
    return valid ? featureKey.featureGroup! : '';
  });
  const [enabled, setEnabled] = useState(featureKey.enabled);
  const [enabledCompanies, setEnabledCompanies] = useState<Company[]>([]);
  const [disabledCompanies, setDisabledCompanies] = useState<Company[]>([]);
  const [initiallyEnabledIds, setInitiallyEnabledIds] = useState<Set<string>>(new Set());
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [enabledSearch, setEnabledSearch] = useState('');
  const [disabledSearch, setDisabledSearch] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const data = await getCompanies() as any[];
      const all: Company[] = data.map(d => ({
        id: d.id,
        name: d.name || 'Unnamed',
        featureKeys: d.featureKeys || [],
        features: d.features || [],
      }));

      const fkId = featureKey.id;
      const fkKv = featureKey.keyValue || '';

      const isEnabled = (c: Company): boolean => {
        if (c.features && c.features.length > 0) {
          // Also check legacy keys that may not yet be normalized
          const inNew = c.features.some(fg => fg.featureKeys.includes(fkId));
          if (inNew) return true;
        }
        // Fallback: check legacy featureKeys by keyValue
        return Array.isArray(c.featureKeys) && c.featureKeys.includes(fkKv);
      };

      const enabled = all.filter(isEnabled);
      setEnabledCompanies(enabled);
      setInitiallyEnabledIds(new Set(enabled.map(c => c.id)));
      setDisabledCompanies(all.filter(c => !isEnabled(c)));
    } catch (err: any) {
      logger.error('Error loading companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const moveToEnabled = (company: Company) => {
    setDisabledCompanies(prev => prev.filter(c => c.id !== company.id));
    setEnabledCompanies(prev => [...prev, company].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const moveToDisabled = (company: Company) => {
    setEnabledCompanies(prev => prev.filter(c => c.id !== company.id));
    setDisabledCompanies(prev => [...prev, company].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const fgId = featureGroup;

      await updateFeatureKey(featureKey.id, {
        name: name.trim(),
        description: description.trim(),
        terms: terms.trim(),
        url: url.trim(),
        displayOrder: displayOrder === '' ? null : Number(displayOrder),
        featureGroup: fgId,
        enabled,
      });

      // Only write to companies whose status actually changed
      const fkId = featureKey.id;
      const newlyEnabled = enabledCompanies.filter(c => !initiallyEnabledIds.has(c.id));
      const newlyDisabled = disabledCompanies.filter(c => initiallyEnabledIds.has(c.id));

      if (newlyEnabled.length > 0 || newlyDisabled.length > 0) {
        // Build keyValue → {id, featureGroup} lookup for legacy normalization
        const kvToGroup = new Map(featureKeysMeta.map(fk => [fk.keyValue, { id: fk.id, featureGroup: fk.featureGroup ?? '' }]));
        await Promise.all([
          ...newlyEnabled.map(company => {
            const normalized = normalizeCompanyFeatures(company, kvToGroup);
            const newFeatures = addFkToFeatures(normalized, fkId, fgId ?? '');
            return updateCompany(company.id, { features: newFeatures });
          }),
          ...newlyDisabled.map(company => {
            const normalized = normalizeCompanyFeatures(company, kvToGroup);
            const newFeatures = removeFkFromFeatures(normalized, fkId, fgId ?? '');
            return updateCompany(company.id, { features: newFeatures });
          }),
        ]);
      }

      toast({ title: 'Feature key updated', description: `"${name.trim()}" has been updated.` });
      onClose();
    } catch (err: any) {
      logger.error('Error updating feature key:', err);
      toast({ title: 'Failed to update feature key', description: err.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredEnabled = enabledCompanies.filter(c =>
    c.name.toLowerCase().includes(enabledSearch.toLowerCase())
  );
  const filteredDisabled = disabledCompanies.filter(c =>
    c.name.toLowerCase().includes(disabledSearch.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={() => { if (!isSubmitting) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            Edit Feature Key
          </DialogTitle>
          <DialogDescription>
            Update this feature key and manage which companies have it enabled.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="efk-name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="efk-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="efk-description">Description</Label>
              <Textarea
                id="efk-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSubmitting}
                rows={3}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="efk-terms">Terms &amp; Conditions</Label>
            <Textarea
              id="efk-terms"
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder="Long terms and conditions text"
              disabled={isSubmitting}
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="efk-url">URL</Label>
              <Input
                id="efk-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="e.g. serviceurltext"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="efk-display-order">Display Order</Label>
              <Input
                id="efk-display-order"
                type="number"
                value={displayOrder}
                onChange={(e) => setDisplayOrder(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="e.g. 1"
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="efk-feature-group">Feature Group</Label>
            {featureGroups.length === 0 ? (
              <p className="text-xs text-slate-400 italic">Add a Feature Group first to assign this key to one.</p>
            ) : (
              <Select value={featureGroup || 'none'} onValueChange={(v) => setFeatureGroup(v === 'none' ? '' : v)} disabled={isSubmitting}>
                <SelectTrigger id="efk-feature-group">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No group</SelectItem>
                  {featureGroups.map(fg => (
                    <SelectItem key={fg.id} value={fg.id}>{fg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {featureKey.keyValue && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Key value:</span>
              <code className="text-xs font-mono bg-slate-100 px-2 py-0.5 rounded text-slate-700">{featureKey.keyValue}</code>
            </div>
          )}

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <Label htmlFor="efk-enabled" className="text-sm font-medium">Enabled</Label>
              <p className="text-xs text-slate-500 mt-0.5">Whether this feature key is active</p>
            </div>
            <Switch
              id="efk-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <Label className="mb-3 block">Company Access</Label>
            {loadingCompanies ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading companies...
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {/* Enabled companies */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-700">
                      Enabled ({enabledCompanies.length})
                    </span>
                  </div>
                  <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      value={enabledSearch}
                      onChange={(e) => setEnabledSearch(e.target.value)}
                      placeholder="Search..."
                      className="pl-8 h-8 text-xs"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="rounded-md border border-green-200 min-h-[160px] max-h-[200px] overflow-y-auto divide-y">
                    {filteredEnabled.length === 0 ? (
                      <p className="text-xs text-slate-400 px-3 py-3 text-center">No companies</p>
                    ) : (
                      filteredEnabled.map(c => (
                        <div key={c.id} className="flex items-center justify-between px-2 py-1.5 hover:bg-green-50 gap-1">
                          <span className="text-xs text-slate-700 truncate flex-1">{c.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-red-500 flex-shrink-0"
                            onClick={() => moveToDisabled(c)}
                            disabled={isSubmitting}
                            title="Disable for this company"
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Disabled companies */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-500">
                      Disabled ({disabledCompanies.length})
                    </span>
                  </div>
                  <div className="relative">
                    <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                    <Input
                      value={disabledSearch}
                      onChange={(e) => setDisabledSearch(e.target.value)}
                      placeholder="Search..."
                      className="pl-8 h-8 text-xs"
                      disabled={isSubmitting}
                    />
                  </div>
                  <div className="rounded-md border min-h-[160px] max-h-[200px] overflow-y-auto divide-y">
                    {filteredDisabled.length === 0 ? (
                      <p className="text-xs text-slate-400 px-3 py-3 text-center">No companies</p>
                    ) : (
                      filteredDisabled.map(c => (
                        <div key={c.id} className="flex items-center justify-between px-2 py-1.5 hover:bg-gray-50 gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-slate-400 hover:text-green-600 flex-shrink-0"
                            onClick={() => moveToEnabled(c)}
                            disabled={isSubmitting}
                            title="Enable for this company"
                          >
                            <ChevronLeft className="h-3.5 w-3.5" />
                          </Button>
                          <span className="text-xs text-slate-700 truncate flex-1">{c.name}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
